import { createResource, createSignal, For, Match, Switch, Show } from "solid-js";
import { useAdminContext } from "./AdminContext";
import socket from "./socket";
import { useI18n } from "./i18n";

export default function AdminEmailTemplates() {
  const t = useI18n();
  const [adminStore, setAdminStore] = useAdminContext();
  const [editingTemplate, setEditingTemplate] = createSignal(null);
  const [subject, setSubject] = createSignal("");
  const [body, setBody] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [message, setMessage] = createSignal(null);

  // Fetch email templates
  const [emailTemplates, { refetch }] = createResource(() => {
    return new Promise((resolve) => {
      socket.emit("admin-get-email-templates", (templates) => {
        resolve(templates);
      });
    });
  });

  // Handle form submission for template editing
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!editingTemplate()) return;

    const template = {
      id: editingTemplate().id,
      subject: subject(),
      body: body(),
      description: description()
    };

    socket.emit("admin-update-email-template", template, (result) => {
      if (result.success) {
        setMessage({ type: "success", text: t("Template updated successfully") });
        refetch();
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: "error", text: t("Failed to update template") });
      }
    });
  };

  // Start editing a template
  const startEdit = (template) => {
    setEditingTemplate(template);
    setSubject(template.subject);
    setBody(template.body);
    setDescription(template.description || "");
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingTemplate(null);
    setSubject("");
    setBody("");
    setDescription("");
  };

  return (
    <div class="container-fluid mt-4">
      <h1>{t("Email Template Management")}</h1>

      <div class="row">
        <div class="col-md-12">
          <Show when={message()}>
            <div class={`alert alert-${message().type === "success" ? "success" : "danger"} mt-3`}>
              {message().text}
            </div>
          </Show>
        </div>
      </div>

      <div class="row">
        <div class="col-md-5">
          <div class="card">
            <div class="card-header">
              <h5>{t("Email Templates")}</h5>
            </div>
            <div class="card-body">
              <Show when={emailTemplates.loading}>
                <p class="text-center">{t("Loading templates...")}</p>
              </Show>

              <Show when={emailTemplates.error}>
                <p class="text-center text-danger">{t("Error loading templates")}</p>
              </Show>

              <Show when={!emailTemplates.loading && !emailTemplates.error}>
                <div class="list-group">
                  <For each={emailTemplates()}>
                    {(template) => (
                      <button
                        class={`list-group-item list-group-item-action ${editingTemplate() && editingTemplate().id === template.id ? 'active' : ''}`}
                        onClick={() => startEdit(template)}
                      >
                        <div class="d-flex w-100 justify-content-between">
                          <h5 class="mb-1">{template.name}</h5>
                        </div>
                        <p class="mb-1">{template.description || t("No description")}</p>
                        <small>{t("Last updated")}: {new Date(template.updated_at).toLocaleString()}</small>
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </div>
        </div>

        <div class="col-md-7">
          <Show when={editingTemplate()}>
            <div class="card">
              <div class="card-header">
                <h5>{t("Edit Template")}: {editingTemplate().name}</h5>
              </div>
              <div class="card-body">
                <form onSubmit={handleSubmit}>
                  <div class="mb-3">
                    <label class="form-label">{t("Description")}</label>
                    <input
                      type="text"
                      class="form-control"
                      value={description()}
                      onInput={(e) => setDescription(e.target.value)}
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label">{t("Subject")}</label>
                    <input
                      type="text"
                      class="form-control"
                      value={subject()}
                      onInput={(e) => setSubject(e.target.value)}
                      required
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label">{t("Body")}</label>
                    <textarea
                      class="form-control"
                      rows="10"
                      value={body()}
                      onInput={(e) => setBody(e.target.value)}
                      required
                    ></textarea>
                    <small class="form-text text-muted">
                      {t("Use placeholders like {{student_name}}, {{teacher_name}}, and {{base_url}}.")}
                    </small>
                  </div>

                  <div class="mb-3">
                    <button type="submit" class="btn btn-primary me-2">{t("Save Changes")}</button>
                    <button type="button" class="btn btn-secondary" onClick={cancelEdit}>{t("Cancel")}</button>
                  </div>
                </form>
              </div>
            </div>
          </Show>

          <Show when={!editingTemplate()}>
            <div class="card">
              <div class="card-body">
                <p class="text-center text-muted">{t("Select a template to edit")}</p>
              </div>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}
